/**
 * Post-quantum hybrid encryption primitives — Week 3 differentiation.
 *
 * Stack:
 *   - ML-KEM-768 (CRYSTALS-Kyber, NIST FIPS 203) for quantum-resistant
 *     key encapsulation
 *   - ML-DSA-65 (CRYSTALS-Dilithium, NIST FIPS 204) for quantum-
 *     resistant signatures over the public-key bundle (prevents
 *     server-side pubkey substitution)
 *   - HKDF-SHA256 for combining classical + PQ shared secrets so an
 *     attacker has to break BOTH RSA AND Kyber to recover the message
 *     key (correct hybrid construction, not a concatenation shortcut)
 *
 * Wire format (per message, per recipient):
 *   encrypted_keys_pq[user_id] = {
 *     rsa:   base64(RSA-OAEP(recipient_rsa_pub, r_32bytes)),
 *     kyber: base64(ML-KEM-768.encapsulate(recipient_kyber_pub).ciphertext),
 *     wk:    base64(AES-GCM(K, aes_key, iv)) — wrapped message key
 *     iv:    base64(12-byte iv used to wrap aes_key)
 *   }
 * where K = HKDF-SHA256(r || ss_q, info="gs-hybrid-v1", 32 bytes).
 *
 * Threat model closed by THIS module:
 *   - Harvest-now-decrypt-later: archived ciphertext stays secret
 *     even if RSA-2048 falls to a CRQC (quantum computer with
 *     Shor's algorithm). Attacker needs BOTH broken simultaneously.
 *   - Server key substitution: verifying each peer's bundle
 *     signature against their pinned Dilithium pubkey prevents the
 *     server from silently swapping their RSA or Kyber pubkey.
 */
import { ml_kem768 } from '@noble/post-quantum/ml-kem.js';
import { ml_dsa65 } from '@noble/post-quantum/ml-dsa.js';

const HYBRID_INFO = new TextEncoder().encode('gs-hybrid-v1');

// ─── Base64 helpers (byte-array friendly) ───────────────────────────
const b64encode = (bytes) => {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
};
const b64decode = (str) => Uint8Array.from(atob(str), (c) => c.charCodeAt(0));

// ─── HKDF-SHA256 via Web Crypto ─────────────────────────────────────
async function hkdfSha256(ikm, info, length = 32, salt = new Uint8Array(32)) {
  const key = await window.crypto.subtle.importKey(
    'raw', ikm, { name: 'HKDF' }, false, ['deriveBits']
  );
  const bits = await window.crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt, info },
    key,
    length * 8
  );
  return new Uint8Array(bits);
}

// ─── Keypair generation ─────────────────────────────────────────────

export function generateKyberKeypair() {
  const k = ml_kem768.keygen();
  return { publicKey: k.publicKey, secretKey: k.secretKey };
}

export function generateDilithiumKeypair() {
  const d = ml_dsa65.keygen();
  return { publicKey: d.publicKey, secretKey: d.secretKey };
}

/**
 * Sign a canonical bundle tying a user's RSA + Kyber public keys to
 * their Dilithium identity. Peers verify this before trusting any
 * other pubkey for this user.
 */
export function signBundle(rsaPubJwk, kyberPub, userId, dilithiumSecret, createdAt) {
  const bundle = {
    user_id: userId,
    rsa_pub: rsaPubJwk,
    kyber_pub: b64encode(kyberPub),
    created_at: createdAt,
  };
  const canonical = canonicalJsonBytes(bundle);
  const sig = ml_dsa65.sign(canonical, dilithiumSecret);
  return {
    kyber_pub_b64: bundle.kyber_pub,
    bundle_sig_b64: b64encode(sig),
    created_at: createdAt,
  };
}

/**
 * Verify a peer's signed bundle. Accepts the server's view of
 * {kyber_pub, dilithium_pub, bundle_sig, rsa_pub, bundle_created_at}
 * and checks the signature against the caller's pinned Dilithium
 * pubkey. Returns true iff the bundle is signed by the claimed
 * identity. Callers MUST pin `trustedDilithiumPub` out of band (e.g.
 * via the existing safety-number flow).
 */
export function verifyBundle(bundle, trustedDilithiumPub) {
  if (!bundle?.bundle_sig || !bundle?.kyber_pub || !bundle?.rsa_pub) return false;
  try {
    const payload = {
      user_id: bundle.user_id,
      rsa_pub: (typeof bundle.rsa_pub === 'string') ? JSON.parse(bundle.rsa_pub) : bundle.rsa_pub,
      kyber_pub: bundle.kyber_pub,
      created_at: bundle.bundle_created_at || null,
    };
    const canonical = canonicalJsonBytes(payload);
    const sig = b64decode(bundle.bundle_sig);
    return ml_dsa65.verify(sig, canonical, trustedDilithiumPub);
  } catch (_e) {
    return false;
  }
}

// ─── Hybrid wrap / unwrap ───────────────────────────────────────────

/**
 * Wrap `aesKeyRaw` (32 bytes) for a single recipient using the
 * hybrid KEM. Returns the per-recipient envelope ready to be stored
 * under `encrypted_keys_pq[recipient_id]`.
 */
export async function hybridWrap(aesKeyRaw, recipientRsaPubJwk, recipientKyberPub) {
  // Classical leg
  const r = window.crypto.getRandomValues(new Uint8Array(32));
  const rsaPub = await window.crypto.subtle.importKey(
    'jwk',
    typeof recipientRsaPubJwk === 'string' ? JSON.parse(recipientRsaPubJwk) : recipientRsaPubJwk,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false, ['encrypt']
  );
  const rsaCt = new Uint8Array(
    await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, rsaPub, r)
  );

  // PQ leg
  const { cipherText: kyberCt, sharedSecret: ssQ } = ml_kem768.encapsulate(recipientKyberPub);

  // Hybrid key = HKDF(r || ss_q)
  const ikm = new Uint8Array(r.length + ssQ.length);
  ikm.set(r, 0);
  ikm.set(ssQ, r.length);
  const kBytes = await hkdfSha256(ikm, HYBRID_INFO, 32);

  // AES-GCM-wrap the message key under K
  const wrapKey = await window.crypto.subtle.importKey(
    'raw', kBytes, { name: 'AES-GCM' }, false, ['encrypt']
  );
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const wk = new Uint8Array(
    await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrapKey, aesKeyRaw)
  );

  return {
    rsa: b64encode(rsaCt),
    kyber: b64encode(kyberCt),
    wk: b64encode(wk),
    iv: b64encode(iv),
  };
}

/**
 * Unwrap the AES key from a hybrid envelope. Returns the raw
 * 32-byte AES key. Throws if unwrap fails — caller should fall back
 * to the classical `encrypted_keys[user_id]` path.
 */
export async function hybridUnwrap(envelope, rsaPrivKey, kyberSecret) {
  const rsaCt = b64decode(envelope.rsa);
  const kyberCt = b64decode(envelope.kyber);
  const wk = b64decode(envelope.wk);
  const iv = b64decode(envelope.iv);

  const r = new Uint8Array(
    await window.crypto.subtle.decrypt({ name: 'RSA-OAEP' }, rsaPrivKey, rsaCt)
  );
  const ssQ = ml_kem768.decapsulate(kyberCt, kyberSecret);

  const ikm = new Uint8Array(r.length + ssQ.length);
  ikm.set(r, 0);
  ikm.set(ssQ, r.length);
  const kBytes = await hkdfSha256(ikm, HYBRID_INFO, 32);

  const unwrapKey = await window.crypto.subtle.importKey(
    'raw', kBytes, { name: 'AES-GCM' }, false, ['decrypt']
  );
  const aesRaw = new Uint8Array(
    await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, unwrapKey, wk)
  );
  return aesRaw;
}

// ─── Utilities ──────────────────────────────────────────────────────

/**
 * Deterministic, minimal JSON encoding so sender and verifier always
 * hash the same bytes. Sorts object keys at every depth.
 */
export function canonicalJsonBytes(obj) {
  const canon = (v) => {
    if (v === null || typeof v !== 'object') return JSON.stringify(v);
    if (Array.isArray(v)) return '[' + v.map(canon).join(',') + ']';
    const keys = Object.keys(v).sort();
    return '{' + keys.map((k) => JSON.stringify(k) + ':' + canon(v[k])).join(',') + '}';
  };
  return new TextEncoder().encode(canon(obj));
}

// Short-fingerprint helper for the Quantum chip UI.
export async function dilithiumFingerprint(pubBytes) {
  const hash = await window.crypto.subtle.digest('SHA-256', pubBytes);
  const hex = Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hex.slice(0, 16);
}

export const b64 = { encode: b64encode, decode: b64decode };
